import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import kebabCase from "lodash/kebabCase";
import isNil from "lodash/isNil";
import Rule from "wonderbly-components/lib/Rule";
import Carousel from "../Carousel";
import RichText from "../Prismic/RichText";
import Slide from "./Slide";
import Theme from "./Theme";
import { optimiseImage, prismicImage } from "src/common/utils/prismic-image";
import { EnhancedAddToCartForm as AddToCart } from "../AddToCart";
import TrustpilotWidget from "../TrustpilotWidget";
import Price from "../Price";
import getPathToAsset from "src/common/utils/get-path-to-asset";
import StarsTheme from "../StarsTheme";
import Tags from "../Tags";
import { replaceTextWithValues } from "src/common/utils/text-replace";
import "./ProductHero.scss";

const themeToHyphenated = (themeName) => kebabCase(themeName);
const sectionClassName = (props) =>
  cx("product-hero", {
    [`product-hero--${themeToHyphenated(props.productTheme)}`]:
      props.productTheme,
    "product-hero--loaded": props.isLoaded,
  });

const buildThumbnails = (props) => {
  if (props.media.fabletDown) {
    return null;
  }
  return props.items.map((item, index) => (
    <img
      key={index}
      loading="lazy"
      src={prismicImage(item.image, { width: 920 })}
      alt={item.image.alt || ""}
    />
  ));
};

const getThemeByBgColor = (bgColor) => {
  if (!bgColor) {
    return "light";
  }
  return parseInt(bgColor.replace("#", ""), 16) > 0xffffff / 2
    ? "light"
    : "dark";
};

const ProductHero = (props) => (
  <section
    className={sectionClassName(props)}
    style={{
      backgroundColor: props.productBackgroundColor,
      backgroundImage: props.productBackgroundColor ? "none" : null,
    }}
  >
    <Theme theme={themeToHyphenated(props.productTheme)} />
    <div className="container-guttered">
      <div className="product-hero-wrapper">
        <div className="product-hero-carousel-wrapper">
          <Carousel
            group="product-hero-carousel"
            thumbnails={buildThumbnails(props)}
            disablePagination={props.isNavigationDisabled}
            disableNavigation={props.isNavigationDisabled}
          >
            {props.items.map((item, index) => (
              <Slide
                key={index}
                type={item.slideType}
                image={item.image}
                videoId={item.videoId}
                setIsCarouselMediaPlaying={props.setIsCarouselMediaPlaying}
              />
            ))}
          </Carousel>
        </div>
        <div
          className="product-hero-content-wrapper"
          itemScope={props.includeSchema}
          itemType={props.includeSchema && "http://schema.org/Product"}
        >
          {props.includeSchema && (
            <div className="product-hero-schema">
              <meta itemProp="sku" content={props.productId} />
              <meta itemProp="name" content={props.productTitle} />
              <meta
                itemProp="description"
                content={props.productDescription[0].text}
              />
            </div>
          )}
          <h1 className="text-giga product-hero-title">{props.productTitle}</h1>
          {props.displayPrice && props.price && (
            <Price
              className="product-hero-price"
              price={props.price}
              priceString={props.productPrice}
              savings={props.savings}
              savingsType="slash"
            />
          )}
          {props.productTagline ? (
            <span className="product-hero-tagline">{props.productTagline}</span>
          ) : (
            props.productTags &&
            props.productTagLocation === "above" && (
              <Tags tags={props.productTags} />
            )
          )}
          <RichText
            fields={props.productDescription}
            className={cx("product-hero-description", {
              [`text-${props.productDescriptionTextSize}`]:
                !!props.productDescriptionTextSize,
            })}
          />
          {props.productTags && props.productTagLocation === "below" && (
            <Tags tags={props.productTags} />
          )}
          {props.productReview && (
            <div className="product-hero-review">
              <Rule type="medium" />
              <div className="product-hero-rating-text">
                {props.productRating &&
                  props.productRatingText &&
                  replaceTextWithValues(props.productRatingText, {
                    rating: props.productRating,
                  })}
                <div className="product-hero-rating">
                  <StarsTheme
                    theme={props.productRatingTheme}
                    stars={props.productRating}
                  />
                </div>
              </div>
              <RichText
                className={cx("product-hero-review-quote", {
                  "product-hero-review-quote--padding":
                    props.productReviewBackgroundColour,
                })}
                fields={props.productReview}
                style={{ backgroundColor: props.productReviewBackgroundColour }}
              />
              <div>
                {props.productReviewLink && props.productReviewHref && (
                  <a
                    className="text-micro"
                    href={props.productReviewHref}
                    onClick={props.handleReviewCtaClick}
                  >
                    {props.productReviewLink}
                  </a>
                )}
                {props.productReviewLinkText &&
                  props.productRatingTheme === "trustpilot" &&
                  props.productRating && (
                    <span className="text-micro">
                      {" "}
                      {props.productReviewLinkText}
                      {props.productRatingTheme === "trustpilot" &&
                        props.productRating && (
                          <img
                            className="trustpilot-icon"
                            src={optimiseImage(
                              getPathToAsset("img/trustpilot-logo.png")
                            )}
                            alt="trustpilot-icon"
                          />
                        )}
                    </span>
                  )}
              </div>
              {props.displayTrustpilotWidget && (
                <TrustpilotWidget
                  templateId="productmini"
                  productId={props.trustPilotSkus}
                  theme={getThemeByBgColor(props.productBackgroundColor)}
                  width="100%"
                  isComponent={false}
                  className="product-hero-trustpilot-productmini"
                />
              )}
            </div>
          )}
          {!isNil(props.addToCart) && <AddToCart {...props} />}
        </div>
      </div>
    </div>
  </section>
);

ProductHero.propTypes = {
  includeSchema: PropTypes.bool.isRequired,
  productBackgroundColor: PropTypes.string,
  productTheme: PropTypes.string,
  items: PropTypes.array.isRequired,
  productId: PropTypes.string.isRequired,
  trustPilotSkus: PropTypes.string.isRequired,
  productTitle: PropTypes.string.isRequired,
  productPrice: PropTypes.string,
  price: PropTypes.object,
  displayPrice: PropTypes.bool.isRequired,
  savings: PropTypes.object,
  productTagline: PropTypes.string,
  productDescription: PropTypes.array.isRequired,
  productReview: PropTypes.array,
  productReviewLink: PropTypes.string,
  productReviewHref: PropTypes.string,
  handleReviewCtaClick: PropTypes.func.isRequired,
  isNavigationDisabled: PropTypes.bool,
  displayTrustpilotWidget: PropTypes.bool.isRequired,
  setIsCarouselMediaPlaying: PropTypes.func.isRequired,
  addToCart: PropTypes.object,
  productTags: PropTypes.array,
  productTagLocation: PropTypes.string,
  productDescriptionTextSize: PropTypes.string,
  productRating: PropTypes.number,
  productRatingText: PropTypes.string,
  productRatingTheme: PropTypes.string,
  productReviewBackgroundColour: PropTypes.string,
  productReviewLinkText: PropTypes.string,
};

export default ProductHero;
